import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Box } from "@mui/material";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../../../shared/components/form-builder/form-builder";
import {
  mapToTransactionCertificateReqDto,
  transactionCertificateReqDto,
} from "../../tc-models/tc-req-dto/transaction-certificate.req_dto";
import { dtrackItService } from "../../tc-service/dtrackit.service";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useFormActions } from "../../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import useNotification from "../../../../../shared/hooks/useNotification";
import { InnerButton } from "../../../../../shared/components/buttons/floating-button-container";
import {
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../shared/app-configs/utilit";
import AlertPopup from "../../../../../shared/components/modals/alert-moadal";
import { RestrictedDataValidation } from "../../../../../shared/utils/custom-validation.utils";

const dateSchema = Yup.string()
  .required("This field is required")
  .matches(
    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
    "Date must be in YYYY-MM-DD format"
  );

const TC_NUMBER_PATTERN = /^[A-Z]{3}-TC-[A-Z0-9]{10}-\d{2}$/;
const RETURN_STATUSES = ["R", "RA", "RI"];
const NON_RETURN_STATUSES = ["V", "A", "I"];
const AMENDED_STATUSES = ["A", "RA"];
const INVALID_STATUSES = ["I", "RI"];
const AMENDMENT_REASONS = ["R", "T", "C", "I", "D", "E"];
const INVALIDATION_REASONS = ["O", "F", "C", "I", "M", "S", "E"];

const normalizeTcNumber = (value?: string) =>
  (value || "").replace(/\u2013/g, "-").trim().toUpperCase();

const isFutureDate = (value?: Date | string | null) => {
  if (!value) return false;
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dateValue.setHours(0, 0, 0, 0);
  return dateValue.getTime() > today.getTime();
};

interface TransactionCertificateProps {
  onNextTab: () => Promise<void>;
}
const TransactionCertificate: React.FC<TransactionCertificateProps> = ({
  onNextTab,
}) => {
  // const getTCDetails = transactionCertificateGetService.transactionCertsGetStore((state) => state.getTCDetails);
  // const tcDetails = transactionCertificateGetService.transactionCertsGetStore((state) => state.tcDetails);
  const [organicFarmStandardsData, setOrganicFarmStandards] = useState([{}]);
  const { TE_ID, formtype } = useParams();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  const { notify, showBanner } = useNotification();
  const navigate = useNavigate();
  const { getCurrentFormValues, updateField } = useFormActions(
    "Transaction-Details"
  );

  const { loading, error, executeGet, executePost } = useApi();
  const [initialValues, setInitalValues] = useState(() => ({
    ...transactionCertificateReqDto,
    tcIsReturn: transactionCertificateReqDto.tcIsReturn || "No",
  }));
  const [existingData, setExistingData] = useState(
    transactionCertificateReqDto
  );
  const [isVersionUpdated, setIsVersionUpdated] = React.useState(false);

  const [getZustandData, setZustandData] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");

  const formRef = useRef<any>(null);
  const {
    setDelayReason,
    setTCIssueDate,
    getEditTCInfo,
    setLabelGrades,
    cbLicensingCode,
    setTCStandardGOTS,
    getTCStandardGOTS,
    setOrgFarmStandards,
    setEditTCInfo,
  } = useTransactionCertificateStore();
  const [statuses, setStatuses] = useState({
    tcStatusOptions: [],
    returnTcStatusOptions: [],
  });

  const handlePlaceOfIssueChange = (value: any) => {
    formRef.current.setFieldValue(
      "tcPlaceOfIssue",
      value ? value.description_1 : ""
    );
  };

  const searchApi = useApi();

  const getOrganicFarmStandards = async () => {
    try {
      const result: any = await dtrackItService.getOrganicFarmStandards();
      const orgFStd = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setOrganicFarmStandards(orgFStd);
      setOrgFarmStandards(orgFStd);
    } catch (err) {
      // navigation debug removed
    }
  };
  const isGotsExists = async (tcStandard: string[]): Promise<boolean> => {
    return tcStandard.length === 1 && tcStandard.includes("GOTS");
  };

  const fetchPlaceDetailsFromAPI = async (query: string) => {
    try {
      const response: any = await searchApi.executeGet(
        TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4,
        {
          query_param: TCAppConfig.GET_STATE_CODE_V4,
          state_desc: query,
        }
      );

      // Ensure data exists and is an array
      const items = Array.isArray(response?.data) ? response.data : [];

      // Removed debug log for response
      return items.map((item: any) => ({
        label: `${item.description_1} (${item.code_1})`, // shown in dropdown
        value: `${item.description_1} (${item.code_1})`, // stored in form
      }));
    } catch (err) {
      console.error("Failed to fetch Place of Issue:", err);
      return [];
    }
  };

  const fetchTcBaseDetails = async () => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_TC_BASE_DETAILS_V4,
        {
          tcId: TE_ID,
        }
      );
      const data = mapToTransactionCertificateReqDto(response.data);
      data.tcCbLicensingCode = cbLicensingCode;

      await getStandardLabelGrade([...data?.tcStandard, "NA"]);
      let gotsValue = await isGotsExists(data.tcStandard);
      setTCStandardGOTS(gotsValue);
      setTCIssueDate(data?.tcIssueDate ?? "");
      setDelayReason(data?.tcDelayReason === "" ? false : true);
      setInitalValues(data);
      setExistingData(data);
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };

  const fetchTcBaseDetailsforSubmitFlow = async (tcid: any) => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_TC_BASE_DETAILS_V4,
        {
          tcId: tcid,
        }
      );
      const data = mapToTransactionCertificateReqDto(response.data);
      data.tcCbLicensingCode = cbLicensingCode;
      setTCIssueDate(data?.tcIssueDate ?? "");
      setDelayReason(data?.tcDelayReason === "" ? false : true);
      setInitalValues(data);
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };
  const setZustandInitialState = (initialState?: any) => {
    const defalutState: any = initialValues;
    //set zustand state inital values
    Object.entries(initialState ?? defalutState).forEach(([key, value]) => {
      updateField(key, value);
    });
  };

  const fetchStatuses = async() => {
    const tcStatusResponse = await dtrackItService.getTCStatus();
    if (tcStatusResponse?.data) {
      const transformedStatusOptions = tcStatusResponse.data.map(
        (item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
        })
      );
      setStatuses(prev=>{
        return {...prev, tcStatusOptions:transformedStatusOptions}
    });
    }
    const retTcStatusResponse = await dtrackItService.getReturnTCStatus();
    if (retTcStatusResponse?.data) {
      const transformedStatusOptions = retTcStatusResponse.data.map(
        (item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
        })
      );
      setStatuses(prev=>{
        return {...prev, returnTcStatusOptions:transformedStatusOptions}
    });
    }
  };
  useEffect(() => {
    var currentState: any = getCurrentFormValues();

    if (Object.keys(currentState).length === 0) {
      setZustandInitialState(null);
    } else {
      setZustandInitialState(currentState);
      setInitalValues(currentState);
    }
  }, [getZustandData]);

  const getStandardLabelGrade = async (value: any) => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_STANDARD_LABEL_GRADE_BY_TCSTANDARD,
        { standard: JSON.stringify(value) }
      );
      if (response.status == 200) {
        const labelGrades = response.data.map((item: any) => ({
          label: item.description_1,
          value: item.code_1,
        }));
        setLabelGrades(labelGrades);
      }
    } catch (err) {
      console.error("Failed to fetch StandardLabelGrade:", err);
    }
  };

  useEffect(() => {
    fetchStatuses();
    if (TE_ID) fetchTcBaseDetails();
    const fetchData = async () => {
      await getOrganicFarmStandards();
    };
    fetchData();
  }, []);

  // Validation function for TC Number and Version Number
  const validateTCNumberOrVersion = (value: string | undefined) => {
    if (!value) return true; // Let required validation handle empty values

    const trimmedValue = normalizeTcNumber(value);
    if (!trimmedValue) {
      return 'Cannot be blank or empty';
    }

    const disallowedValues = ['n/a', 'na', 'null', 'not applicable', 'not available'];
    const lowerValue = trimmedValue.toLowerCase();
    if (disallowedValues.includes(lowerValue)) {
      return 'Disallowed values: n/a, na, null, not applicable, not available are not permitted';
    }

    if (trimmedValue.startsWith('CB-')) {
      return 'Cannot start with "CB-" prefix';
    }

    if (!TC_NUMBER_PATTERN.test(trimmedValue)) {
      return 'Expected format XXX-TC-##########-00 using uppercase letters A-Z and numbers 0-9';
    }

    if (cbLicensingCode && cbLicensingCode !== `CB-${trimmedValue.slice(0, 3)}`) {
      return 'TC number prefix must match the CB licensing code';
    }

    return true;
  };

  const transactionCertificateValidationService = {
    transactioncertsValidation: Yup.object().shape({
      tcNo: Yup.string()
        .required("Transaction Certificate Number is required")
        .test('tc-number-validation', function(value) {
          const result = validateTCNumberOrVersion(value);
          return result === true ? true : this.createError({ message: result });
        }),
      tcStandard: Yup.array()
        .min(1, "Select at least one TC Standard")
        .required("TC Standard is required"),
      tcIssueDate: Yup.date()
        .required("TC Date of Issue is required")
        .test(
          "date-of-issue-not-future",
          "TC Issue Date cannot be in the future",
          function (value) {
            return !isFutureDate(value);
          }
        )
        .test(
          "date-of-issue-validation",
          "TC cannot be issued after the TC is last updated",
          function (value) {
            const { tcLastUpdatedDate } = this.parent;
            if (!value || !tcLastUpdatedDate) return true;
            return (
              new Date(value).getTime() <= new Date(tcLastUpdatedDate).getTime()
            );
          }
        ),
      tcVersionNo: Yup.string()
        .required("TC Version Number is required")
        .test('tc-version-validation', function(value) {
          const result = validateTCNumberOrVersion(value);
          return result === true ? true : this.createError({ message: result });
        })
        .test("tc-version-prefix-validation", function(value) {
          const { tcNo } = this.parent;
          const normalizedTcNo = normalizeTcNumber(tcNo);
          const normalizedVersion = normalizeTcNumber(value);
          if (!normalizedTcNo || !normalizedVersion) return true;
          if (!TC_NUMBER_PATTERN.test(normalizedTcNo) || !TC_NUMBER_PATTERN.test(normalizedVersion)) return true;
          if (normalizedTcNo.slice(0, -2) !== normalizedVersion.slice(0, -2)) {
            return this.createError({
              message: "Only the last two digits are allowed to change in the version number",
            });
          }
          return true;
        }),
      tcStatus: Yup.string()
        .required("TC Status is required")
        .test(
          "tcStatus-validation",
          "Invalid TC status",
          function (value) {
            const { tcNo, tcVersionNo, tcIsReturn } = this.parent;
            if (!value) return true;
            const isReturn = tcIsReturn === "Yes";
            if (isReturn && !RETURN_STATUSES.includes(value)) {
              return this.createError({
                message: "Return TCs allow only R, RA, or RI",
              });
            }
            if (!isReturn && !NON_RETURN_STATUSES.includes(value)) {
              return this.createError({
                message: "Non-return TCs allow only V, A, or I",
              });
            }

            const normalizedTcNo = normalizeTcNumber(tcNo);
            const normalizedVersion = normalizeTcNumber(tcVersionNo);
            const versionChanged = normalizedTcNo && normalizedVersion && normalizedTcNo !== normalizedVersion;
            const suffixChanged = normalizedVersion && normalizedVersion.slice(-2) !== "00";
            if (AMENDED_STATUSES.includes(value) && !versionChanged) {
              return this.createError({
                message: "Amended statuses require the TC Version Number to differ from the TC Number",
              });
            }
            if ((versionChanged || suffixChanged) && !AMENDED_STATUSES.includes(value)) {
              return this.createError({
                message: "Version number changes require Amended or Return Amended status",
              });
            }
            return true; // Validation passes
          }
        ),
      tcAmendmentReason: Yup.string().nullable().test("tc-amendment-reason", function(value) {
        const { tcStatus } = this.parent;
        if (AMENDED_STATUSES.includes(tcStatus) && !value) {
          return this.createError({ message: "Amendment Reason is required" });
        }
        if (!AMENDED_STATUSES.includes(tcStatus) && value) {
          return this.createError({ message: "Amendment Reason must be blank unless status is A or RA" });
        }
        if (value && !AMENDMENT_REASONS.includes(value)) {
          return this.createError({ message: "Allowed Amendment Reason values are R, T, C, I, D, or E" });
        }
        return true;
      }),
      tcInvalidationReason: Yup.string().nullable().test("tc-invalidation-reason", function(value) {
        const { tcStatus } = this.parent;
        if (INVALID_STATUSES.includes(tcStatus) && !value) {
          return this.createError({ message: "Invalidation Reason is required" });
        }
        if (!INVALID_STATUSES.includes(tcStatus) && value) {
          return this.createError({ message: "Invalidation Reason must be blank unless status is I or RI" });
        }
        if (value && !INVALIDATION_REASONS.includes(value)) {
          return this.createError({ message: "Allowed Invalidation Reason values are O, F, C, I, M, S, or E" });
        }
        return true;
      }),
      tcPlaceOfIssue: Yup.string()
        .required("TC Place of Issue is required")
        .max(255, "Maximum 255 Characters are Allowed"),

      tcLastUpdatedDate: Yup.date()
        .required("TC Last Updated is required")
        .test(
          "last-updated-not-future",
          "TC Last Updated Date cannot be in the future",
          function (value) {
            return !isFutureDate(value);
          }
        )
        .test(
          "last-updated-after-issue",
          "TC Last Updated Date must be greater than or equal to TC Issue Date",
          function (value) {
            const { tcIssueDate } = this.parent;
            if (!value || !tcIssueDate) return true;
            return new Date(value).getTime() >= new Date(tcIssueDate).getTime();
          }
        )
        .test(
          "last-updated-changed-for-non-valid",
          "TC Last Updated Date must differ from TC Issue Date when TC Status is not Valid",
          function (value) {
            const { tcIssueDate, tcStatus } = this.parent;
            if (!value || !tcIssueDate || tcStatus === "V") return true;
            return new Date(value).getTime() !== new Date(tcIssueDate).getTime();
          }
        ),

      tcOrganicFarmStandard: Yup.array().when("tcStandard", {
        is: (tcStandard: any) =>
          Array.isArray(tcStandard) && tcStandard.includes("OCS"),
        then: Yup.array()
          .min(1, "Select at least one Organic Farm Standard")
          .required("Select at least one Organic Farm Standard"),
        otherwise: Yup.array()
          .nullable()
          .test("organic-farm-empty-without-ocs", "Organic Farm Standard must be blank unless OCS is selected", (value) => {
            return !Array.isArray(value) || value.length === 0;
          }),
      }),
      tcApplicationReceivedDate: Yup.date().required(
        "Received Date is required"
      ),
      tcApplicationCompleteDate: Yup.date()
        .required("Completion Date is required")
        .test(
          "complete-after-received",
          "TC Application Complete Date must be greater than or equal to TC Application Received Date",
          function (value) {
            const { tcApplicationReceivedDate } = this.parent;
            if (!value || !tcApplicationReceivedDate) return true;
            return new Date(value).getTime() >= new Date(tcApplicationReceivedDate).getTime();
          }
        ),
      tcUsdaNopCompliantFlag: Yup.string()
        .nullable()
        .test(
          "UsdaNopCompliantFlag-required",
          "Certification of the organic material complies with USDA NOP rules is required when OCS is selected",
          function (value) {
            const { tcStandard } = this.parent;
            const isOCSSelected =
              Array.isArray(tcStandard) && tcStandard.includes("OCS");
            if (isOCSSelected && !value) {
              return this.createError({
                message:
                  "Certification of the organic material complies with USDA NOP rules is required",
              });
            }
            if (!isOCSSelected && value) {
              return this.createError({
                message:
                  "Certification of the organic material complies with USDA NOP rules must be blank unless OCS is selected",
              });
            }

            return true;
          }
        ),
      tcDelayReason: Yup.string()
        .required("Delay Reason is required; select NA if not applicable")
        .test('no-restricted-data', 'N/A, Not available, n/a are not allowed. Use "na" option if not applicable', 
          (value) => {
            if (!value) return true;
            const trimmedValue = value.trim();
            // Allow "NA" as it's a valid option in the dropdown
            if (trimmedValue === "na") return true;
            // Use the imported function to check for other restricted values
            // The function checks for "NA", "N/A", "Not available", "n/a"
            // Since we already allowed "NA" above, this will catch the other variations
            return !RestrictedDataValidation.isRestrictedData(value);
          }),
      tcIsReturn: Yup.string().required(),
    }),
  };

  const formConfig = {
    pageName: "Transaction-Details",
    initialValues,
    validationSchema:
      transactionCertificateValidationService.transactioncertsValidation,
    sections: [
      {
        title: "Standards and Certifications",
        fields: [
          {
            label: "Select Standard",
            name: "tcStandard",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                let gotsValue = await isGotsExists(updatedValue);
                // //alert(gotsValue)
                setTCStandardGOTS(gotsValue);
              },
            },
            props: {
              multiple: true,
              enableSearch: true,
              options: [
                { label: "Organic Content Standard (OCS)", value: "OCS" },
                { label: "Responsible Down Standard (RDS)", value: "RDS" },
                { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
                { label: "Global Recycled Standard (GRS)", value: "GRS" },
                { label: "Responsible Wool Standard (RWS)", value: "RWS" },
                { label: "Content Claim Standard (CCS)", value: "CCS" },
                { label: "Recycled Claim Standard (RCS)", value: "RCS" },
                { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
                // { label: "Sustainable Fiber Alliance (SFA)", value: "SFA" },
                // {
                //   label: "Global Organic Textile Standard (GOTS)",
                //   value: "GOTS",
                // },
              ],
              placeholder: "Select one or more options",
            },
          },
          {
            label: "Select Organic Farm Standard",
            name: "tcOrganicFarmStandard",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              return (
                Array.isArray(value?.tcStandard) &&
                value.tcStandard.includes("OCS")
              );
            },
            readOnly: hasSubmitTc,
            visible: (value: any) =>
              Array.isArray(value?.tcStandard) &&
              value.tcStandard.includes("OCS"),
            props: {
              multiple: true,
              options: organicFarmStandardsData,
              placeholder: "Select one or more options",
            },
          },
          {
            label:
              "Certification of the organic material complies with USDA NOP rules",
            name: "tcUsdaNopCompliantFlag",
            type: "radio",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              return (
                Array.isArray(value?.tcStandard) &&
                value.tcStandard.includes("OCS")
              );
            },
            readOnly: hasSubmitTc,
            visible: (value: any) =>
              Array.isArray(value?.tcStandard) &&
              value.tcStandard.includes("OCS"),
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
          },
        ],
      },
      {
        title: "Transaction Certificate Information",
        fields: [
          {
            label: "Transaction Certificate Number",
            name: "tcNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            disabled:
              getEditTCInfo?.tcId === TE_ID && getEditTCInfo?.isParentStatus
                ? true
                : false,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "Enter Transaction Certificate Number",
              variant: "outlined",
              fullWidth: true,
            },
            events: {
              onChange: (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                const normalizedValue = normalizeTcNumber(updatedValue);
                setFieldValue("tcNo", normalizedValue);
                setFieldValue("tcVersionNo", normalizedValue);
              },
            },
          },
          {
            label: "Transaction Certificate Version Number",
            name: "tcVersionNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "Enter Version Number",
              variant: "outlined",
              fullWidth: true,
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                const normalizedValue = normalizeTcNumber(value);
                setFieldValue("tcVersionNo", normalizedValue);
                if (hasSubmitTc) setIsVersionUpdated(true);
              },
            },
          },
          {
            label: "Is it a Return Transaction Certificate?",
            name: "tcIsReturn",
            type: "radio",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                // Reset status field when return type changes
                setFieldValue("tcStatus", "");
                // Force re-render by updating the field
                setFieldValue("tcIsReturn", value);
              },
            },
          },
          {
            label: "Status",
            name: "tcStatus",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: (formValues: any) => {
              const isReturn = formValues?.tcIsReturn === "Yes";
              return {
                options: isReturn
                  ? statuses?.returnTcStatusOptions
                  : statuses?.tcStatusOptions,
                placeholder: "Select Status",
                size: "small",
              };
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                setFieldValue("tcStatus", value);
                setFieldValue("tcAmendmentReason", "");
                setFieldValue("tcInvalidationReason", "");
              },
            },
          },
          {
            label: "Delay Reason",
            name: "tcDelayReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            helperText: 'Please select "NA" if not applicable',
            props: {
              options: [
                { value: "A", label: "A: acceptable animal fiber exception" },
                {
                  value: "D",
                  label:
                    "D: acceptable transport documentation authentication exception",
                },
                {
                  value: "E",
                  label: "E: exemption issued by Textile Exchange",
                },
                {
                  value: "F",
                  label:
                    "F: acceptable sellers with final products as inputs exception",
                },
                {
                  value: "I",
                  label: "I: input transaction certificate issued late",
                },
                { value: "na", label: "NA: Not applicable" },
              ],
              placeholder: "Select Delay Reason",
              size: "small",
            },
          },
          {
            label: "Amendment Reason",
            name: "tcAmendmentReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              return AMENDED_STATUSES.includes(value?.tcStatus);
            },
            visible: (value: any) => AMENDED_STATUSES.includes(value?.tcStatus),
            readOnly: hasSubmitTc,
            props: {
              options: [
                {
                  value: "R",
                  label:
                    "R:Reduction - reduction in the quantity of claimed material",
                },
                {
                  value: "T",
                  label:
                    "T:Typographical errors - typographical errors within fourteen calendar days of issuance",
                },
                { value: "C", label: "C:Certification body errors" },
                { value: "I", label: "I:Incorrect information" },
                { value: "D", label: "D:Duplicate transaction certificate" },
                { value: "E", label: "E:Exemption issued by Textile Exchange" },
              ],
              placeholder: "Select Amendment Reason",
              size: "small",
            },
          },
          {
            label: "Invalidation Reason",
            name: "tcInvalidationReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            // disabled: (value: any) => value.tcStatus === 'V' || value.tcStatus === 'A',
            required: (value: any) => {
              return INVALID_STATUSES.includes(value?.tcStatus);
            },
            visible: (value: any) => INVALID_STATUSES.includes(value?.tcStatus),
            readOnly: hasSubmitTc,
            props: {
              options: [
                { value: "F", label: "F: Falsified Or Fraudulent Documents" },
                {
                  value: "E",
                  label:
                    "E: Claimed materials are/will be included in an e-transaction",
                },
                {
                  value: "I",
                  label: "I: Applicable Input Transaction Has Been Invalidated",
                },
                {
                  value: "M",
                  label: "M: Claimed Materials Are Shown To Be Ineligible",
                },
                { value: "S", label: "S: Requests From The Seller" },
                { value: "O", label: "O: Other Reason" },
                { value: "C", label: "C: Certification body errors" },
              ],
              placeholder: "Select Invalidation Reason",
              size: "small",
            },
          },
        ],
      },
      {
        title: "Issuance and Application Information",
        fields: [
          {
            label: "Place of Issue",
            name: "tcPlaceOfIssue",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            value: initialValues.tcPlaceOfIssue, // must match selected value
            props: {
              placeholder: "Enter Place of Issue",
              fullWidth: true,
              variant: "outlined",
              displayKey: "label", // must match property in mapped objects
              onChange: (selected: any) => {
                // selected is the full object returned from onSearch
                const formattedValue = selected?.value ?? "";
                // Save only the value (e.g., "India (IN)")
                formRef.current.setFieldValue("tcPlaceOfIssue", formattedValue);
              },
              onSearch: async (query: string) => {
                // Just call fetchPlaceDetailsFromAPI and return the result
                return await fetchPlaceDetailsFromAPI(query);
              },
            },
          },
          {
            label: "TC Issue Date",
            name: "tcIssueDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
          },
          {
            label: "TC Last Updated Date",
            name: "tcLastUpdatedDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
          },
          {
            label: "TC Application Received Date",
            name: "tcApplicationReceivedDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
          },
          {
            label: "TC Application Complete Date",
            name: "tcApplicationCompleteDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      try {
        const transactionCertsData = mapToTransactionCertificateReqDto(
          values,
          TE_ID,
          isVersionUpdated
        );
        transactionCertsData.tcCbLicensingCode = cbLicensingCode;
        const isTcNoChanged = existingData.tcNo !== transactionCertsData.tcNo;
        const isVersionNoChanged =
          existingData.tcVersionNo !== transactionCertsData.tcVersionNo;
        const isNewlyCreated = TE_ID ? "No" : "Yes";
        const finalUrl = `${TCAppConfig.POST_TC_BASE_DETAILS_V4}?${
          TE_ID ? `tcId=${TE_ID}&` : ""
        }isNewlyCreated=${isNewlyCreated}${
          isTcNoChanged || isVersionNoChanged
            ? `&oldTcNo=${existingData.tcNo}&oldTcVersionNo=${existingData.tcVersionNo}`
            : ""
        }`;
        const result: any = await executePost(finalUrl, transactionCertsData);

        if (result?.status === 200) {
          notify("Saved Succesfully", "success");

          // Get the TC ID for navigation (use new ID if created, otherwise existing)
          const tcId = result.data?.tcId || TE_ID;

          // Ensure Zustand and local form state are in sync
          setZustandInitialState(transactionCertsData);
          setInitalValues(transactionCertsData);

          // Let parent refresh completion map
          await onNextTab();

          // If we are in submitted flow, parent may expect specific routes
          if (hasSubmitTc) {
            if (!isVersionUpdated) {
              navigate(
                `/v4/tc/tc-details/buyer-seller-products/${tcId}`
              );
            } else {
              navigate(
                `/v4/tc/tc-details/transaction-certificate/${tcId}`
              );
              if (result.data?.tcId)
                fetchTcBaseDetailsforSubmitFlow(result.data.tcId);
            }
          } else {
            // For regular create/edit flow, navigate to next tab
            navigate(
              `/v4/tc/${formtype || "create-tc"}/buyer-seller-products/${tcId}`
            );
          }
        } else {
          notify("Unable to Save", "error");
        }
        // Debug: response handle submit removed
      } catch (error) {
        console.error("Error saving Transaction Certificate:", error);
        notify("Error saving data, please try again", "error");
      }
    },
  };

  const handleSubmit = () => {
    if (hasSubmitTc) {
      if (!isVersionUpdated) {
        onNextTab?.();
      } else if (formRef.current) {
        formRef.current.submitForm();
      }
    } else if (formRef.current) {
      formRef.current.submitForm();
    }
  };
  const handleCancelAmendment = async () => {
    try {
      await executePost(TCAppConfig.POST_TC_CANCEL_AMEND_V4, {
        tcId: TE_ID,
        tcCbLicensingCode: cbLicensingCode,
      });
      // if()
      notify("Cancelled Amendment Successfully", "success");
    } catch (err) {
      console.error("Failed to cancel amendment", err);
    }
    navigate(`/v4/tc-dashboard`);
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      {/* Render the FormBuilder component and pass formRef and onSubmit handler */}
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <FormBuilder
            formConfig={formConfig}
            onSubmit={formConfig.handleSubmit}
            formRef={formRef}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem",
            }}
          >
            {getEditTCInfo?.isParentStatus && (
              <InnerButton
                onClick={() => {
                  setDialogOpen(true);
                  setPopUpTitle("Cancel Amendment");
                  setPopUpMessage(
                    "Are you sure you want to cancel this amendment?"
                  );
                  setPopUpBtnName("Confirm");
                }}
              >
                Cancel Amendment
              </InnerButton>
            )}
            {!hasSubmitTc ? (
              <NavLink to="/v4/tc-dashboard">
                <InnerButton>Home</InnerButton>
              </NavLink>
            ) : null}
            <InnerButton onClick={handleSubmit}>
              {hasSubmitTc
                ? !isVersionUpdated
                  ? "Next"
                  : "Save"
                : "Save & Next"}
            </InnerButton>
          </Box>
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleCancelAmendment}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
        </>
      )}
    </Box>
  );
};

export default TransactionCertificate;
